/**
 * AdoraCare - Walk-in right live-activity rail (S2-P5C dashboard redesign;
 * panels wired live in S2-P5E-B1).
 *
 * PURE presentational right column: a live "Recent walk-ins" list and a live
 * "Today's plan" appointments card.
 *
 * DATA HONESTY:
 *   - Recent walk-ins are LIVE from workstation data (name / source / time /
 *     status). The workstation payload carries NO phone (admin-safe), so rows
 *     show no phone - never fabricated. Each recorded row's status is
 *     "Registered" (the only state the data tracks). Honest empty state when
 *     there are none today, or an "unavailable" note when the feed is dark.
 *   - Today's plan is LIVE from the standalone GET /api/appointments/today read
 *     (time / name / status / specialty - PHI-bounded, no phone, no clinical
 *     field). An honest "No appointments scheduled" empty state when the clinic
 *     has none today, and an "unavailable" note if that read could not load. No
 *     schedule is ever invented.
 *
 * Rendered via react-dom/server in tests (no jsdom).
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import type {
  TodayAppointment,
  WorkstationRecentWalkin,
} from '@/dashboard/types';
import {
  dayBoardPatientLabel,
  dayStatusChip,
} from '@/dashboard/appointments/ClinicDayBoard';
import { AcBadge, type AppointmentStatusLabelOverrides } from '@/dashboard/ui';
import { formatIstTime } from '@/lib/datetime-ist';
// ITEM-7 CORRECTIVE (2026-09-10): this rail row and the correction door's
// own "Currently recorded" line must show the SAME name for the same
// source value -- `humanizeSource` (ExistingLeadMatches.tsx) reads a
// DIFFERENT vocabulary (LEAD_SOURCE_OPTIONS) than the one the correction
// door and its backend validator actually use (WALKIN_SOURCE_OPTIONS),
// which is exactly how this row showed "Walk-In" while the door's own
// "Currently recorded" showed "Walked in / nearby" for the identical
// stored value. `sourceLabel` is the ONE resolver both now share.
import { sourceLabel } from './WalkinSourceCorrectionDoor';

/**
 * FRONT-OFFICE-DENSITY-PASS-V1 (D4): every recorded walk-in IS registered --
 * that is the only state the walk-in data tracks. It was rendered as a per-row
 * pill, so one fact repeated once per row. It now renders EXACTLY ONCE, as the
 * list's own qualifier.
 */
export const RECENT_WALKINS_ALL_REGISTERED_COPY = 'All registered';

interface WalkinActivityPanelProps {
  /** False when the workstation feed is dark/errored. */
  available: boolean;
  /** Recent walk-ins for the active clinic (LIVE; may be empty). */
  recentWalkins: WorkstationRecentWalkin[];
  /** FD-42 (SAY IT ONCE): today's walk-in COUNT renders on THIS panel's
   *  header (the retired Home tile's fact re-homed). Null = feed dark --
   *  the honest dash. */
  todayWalkinCount?: number | null;
  /** Today's upcoming appointments for the active clinic (LIVE; may be empty). */
  appointments: TodayAppointment[];
  /** False when the appointments read is loading/errored (count not knowable). */
  appointmentsAvailable: boolean;
  /** Target for "View all", or null to omit the link (role cannot see it). */
  viewAllHref: string | null;
  /** TRAIN-B: false when the clinic DAY BOARD renders on the same page --
   *  the plan card is then omitted (one day surface, never two renderings
   *  of the same truth). Defaults to true (non-board roles keep it). */
  showPlan?: boolean;
  /** T7 (SHIPPED-WORK-TRUTH-CORRECTIVE-V1, founder ruling 2026-08-15): the
   *  org's status display-label overrides -- the plan row's status now
   *  renders through THE ONE dayStatusChip canon (the board's chip owner),
   *  so this card can never disagree with the board on a status word.
   *  Absent = canon defaults. (The former LOCAL statusLabel map -- a
   *  second label home that ignored org overrides -- is RETIRED.) */
  statusLabels?: AppointmentStatusLabelOverrides | null;
  /** X12 (Lane 3, 2026-09-10) slot: the source-correction door for a
   *  recent walk-in row (the host supplies it only for a role authorized
   *  to correct -- absent = no door, byte-identical rail). */
  sourceCorrectionAction?: (walkin: WorkstationRecentWalkin) => ReactNode;
}

/** First letters of the first two name words, uppercased; "?" when empty. */
function initialsOf(name: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.charAt(0) ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + second).toUpperCase() || '?';
}

export default function WalkinActivityPanel({
  available,
  recentWalkins,
  todayWalkinCount,
  appointments,
  appointmentsAvailable,
  viewAllHref,
  showPlan = true,
  statusLabels = null,
  sourceCorrectionAction,
}: WalkinActivityPanelProps): JSX.Element {
  const hasRecent = available && recentWalkins.length > 0;
  const hasAppointments = appointmentsAvailable && appointments.length > 0;

  return (
    <div className={`walkin-rail${showPlan ? '' : ' walkin-rail--single'}`}>
      <section className="rail-card" aria-label="Recent walk-ins">
        <div className="rail-card__head">
          <h3 className="rail-card__title">
            Recent walk-ins
            {todayWalkinCount !== undefined ? (
              <span className="rail-card__count" data-testid="walkins-today-count">
                {todayWalkinCount === null ? '-' : todayWalkinCount} today
              </span>
            ) : null}
          </h3>
          {hasRecent ? (
            <span
              className="rail-card__qualifier"
              data-testid="recent-walkins-all-registered"
            >
              {RECENT_WALKINS_ALL_REGISTERED_COPY}
            </span>
          ) : null}
          {viewAllHref ? (
            <Link to={viewAllHref} className="rail-card__link">
              View all
            </Link>
          ) : null}
        </div>
        {hasRecent ? (
          <ul className="recent-list">
            {recentWalkins.map((w) => (
              <li key={String(w.id)} className="recent-row">
                <span className="recent-row__avatar" aria-hidden="true">
                  {initialsOf(w.patientName)}
                </span>
                <span className="recent-row__body">
                  <span className="recent-row__name">
                    {w.patientName ?? 'Unknown patient'}
                  </span>
                  <span className="recent-row__meta">
                    {w.source ? (
                      <span className="recent-row__source">
                        {sourceLabel(w.source)}
                      </span>
                    ) : null}
                    {formatIstTime(w.walkedInAt) ? (
                      <span className="recent-row__time">
                        {formatIstTime(w.walkedInAt)}
                      </span>
                    ) : null}
                  </span>
                  {sourceCorrectionAction ? sourceCorrectionAction(w) : null}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rail-empty" role="status">
            {available
              ? 'No walk-ins yet today.'
              : 'Recent walk-ins are unavailable right now.'}
          </p>
        )}
      </section>

      {showPlan ? (
      <section className="rail-card" aria-label="Today's plan">
        <div className="rail-card__head">
          <h3 className="rail-card__title">Today&apos;s plan</h3>
          <span className="rail-card__link rail-card__link--muted">
            View full day
          </span>
        </div>
        {hasAppointments ? (
          <ul className="plan-list">
            {appointments.map((a) => (
              <li key={String(a.id)} className="plan-row">
                <span className="plan-row__time">
                  {formatIstTime(a.appointmentTime) || '--:--'}
                </span>
                <span className="plan-row__body">
                  {/* D4: THE ONE patient-label owner (dayBoardPatientLabel) --
                      golden-record-first, and NEVER "Unknown patient" (a second
                      local fallback here was a divergent label owner). */}
                  <span className="plan-row__name">
                    {dayBoardPatientLabel(a)}
                  </span>
                  {a.specialty ? (
                    <span className="plan-row__specialty">{a.specialty}</span>
                  ) : null}
                </span>
                {/* T7: THE ONE status-chip canon (dayStatusChip + AcBadge +
                    org overrides) -- the board's own idiom; the hand-rolled
                    pill and its second label map are retired. */}
                <AcBadge tone={dayStatusChip(a.status, statusLabels).tone}>
                  {dayStatusChip(a.status, statusLabels).label}
                </AcBadge>
              </li>
            ))}
          </ul>
        ) : appointmentsAvailable ? (
          <div className="plan-empty" role="status">
            <CalendarDays
              className="plan-empty__icon"
              size={20}
              aria-hidden="true"
            />
            <p className="plan-empty__title">No appointments scheduled</p>
            <p className="plan-empty__sub">Nothing booked for today yet.</p>
          </div>
        ) : (
          <p className="rail-empty" role="status">
            Today&apos;s plan is unavailable right now.
          </p>
        )}
      </section>
      ) : null}
    </div>
  );
}
